/**
 * Magellania CRM — ESM bootstrap entry-point.
 *
 * Phase 3 of Vite/ESM migration (Sprint 6, 2026-05-07).
 * См. docs/dev/vite-migration-plan.md
 *
 * Этот файл подключается через `<script type="module" src="/public/js/entry.js">`
 * в index.html и заменяет 11 classic-script тегов одним ESM entry.
 *
 * ВАЖНО: каждый imported файл — side-effect-only module:
 *   - на верхнем уровне определяет class X { ... }
 *   - в самом конце делает `window.X = X` для совместимости с inline onclick
 *     и с классическими ссылками (init.js, app.js)
 *
 * Порядок импортов соответствует прежнему порядку <script> тегов.
 * Vite в dev-режиме резолвит каждый путь как HTTP-запрос; в prod build
 * inlines в bundle и переименовывает с хешем.
 *
 * Vendor scripts (Lucide, Quill) остаются как обычные `<script src=...>`
 * в `<head>` — они должны загрузиться синхронно ДО этого модуля, чтобы
 * `window.lucide` / `window.Quill` были доступны к моменту выполнения.
 *
 * Auth-guard inline `<script>` в `<head>` (sync redirect ДО рендера body)
 * также не трогается — он критичен и не может быть deferred.
 */

// ─── 1. Utilities ────────────────────────────────────────────────────────────
//   Изначально загружались первыми в <head>. Внутри IIFE определяют
//   функции и присваивают window.{StringUtils,DomUtils,CalcUtils,FormatUtils}.
import './utils/string-utils.js';
import './utils/dom-utils.js';
import './utils/calc-utils.js';
import './utils/format-utils.js';

// ─── 2. Main app class definition ────────────────────────────────────────────
//   Изначально загружался ближе к </body>. Определяет класс
//   `ProfessionalQuoteCalculator` и выставляет на window для init.js
//   и для inline onclick="window.QuoteCalc.X()".
//   Phase 4: класс теперь named-export, можно импортировать.
//   Side-effect import также нужен — в файле есть top-level event listeners
//   и `window.ProfessionalQuoteCalculator = ProfessionalQuoteCalculator`.
import { ProfessionalQuoteCalculator } from './app.js';

// ─── 2a. Domain modules (Phase 5) ────────────────────────────────────────────
//   Каждый модуль экспортирует attach() — добавляет методы на prototype
//   через Object.assign. Должен выполниться ДО `new ProfessionalQuoteCalculator()`
//   в init.js (что и происходит, т.к. init.js импортируется ниже).
import { attachCalculations } from './app/calculations.js';
import { attachAutosave } from './app/autosave.js';
import { attachBulkOperations } from './app/bulk-operations.js';
import { attachQuillEditors } from './app/quill-editors.js';
import { attachResetPrint } from './app/reset-print.js';
import { attachChecklist } from './app/checklist.js';
import { attachRenderSections } from './app/render-sections.js';
import { attachCatalogTemplates } from './app/catalog-templates.js';
import { attachStatsTracking } from './app/usage-stats.js';
import { attachRenderCategories } from './app/render-categories.js';
import { attachFileMenuCategories } from './app/file-menu-categories.js';
import { attachRenderServicesList } from './app/render-services-list.js';
import { attachEventBinding } from './app/event-binding.js';
import { attachCatalogModals } from './app/catalog-modals.js';
import { attachCurrencyShim } from './app/currency-shim.js';
// Sprint 15 C.3 part 1 — app.js modularization (2026-05-10):
import { attachModals } from './app/modals.js';
import { attachCollapsibles } from './app/collapsibles.js';
import { attachKeyboard } from './app/keyboard.js';
import { attachServiceFields } from './app/service-fields.js';
import { attachRegions } from './app/regions.js';
attachCalculations(ProfessionalQuoteCalculator);
attachAutosave(ProfessionalQuoteCalculator);
attachBulkOperations(ProfessionalQuoteCalculator);
attachQuillEditors(ProfessionalQuoteCalculator);
attachResetPrint(ProfessionalQuoteCalculator);
attachChecklist(ProfessionalQuoteCalculator);
attachRenderSections(ProfessionalQuoteCalculator);
attachCatalogTemplates(ProfessionalQuoteCalculator);
attachStatsTracking(ProfessionalQuoteCalculator);
attachRenderCategories(ProfessionalQuoteCalculator);
attachFileMenuCategories(ProfessionalQuoteCalculator);
attachRenderServicesList(ProfessionalQuoteCalculator);
attachEventBinding(ProfessionalQuoteCalculator);
attachCatalogModals(ProfessionalQuoteCalculator);
// Sprint 15 C.3 part 1 attachments
attachModals(ProfessionalQuoteCalculator);
attachCollapsibles(ProfessionalQuoteCalculator);
attachKeyboard(ProfessionalQuoteCalculator);
attachServiceFields(ProfessionalQuoteCalculator);
attachRegions(ProfessionalQuoteCalculator);
// Phase 8c: multi-currency shim — должен быть ПОСЛЕДНИМ, оборачивает
// уже прикреплённые calculations / autosave / render методы.
attachCurrencyShim(ProfessionalQuoteCalculator);
console.log('[entry] 20 domain modules attached');

// ─── 3. API client + offline / sync infrastructure ───────────────────────────
//   Файлы лежат в КОРНЕ проекта (не в /public/), сохраняем оригинальные
//   пути через relative-import. Каждый класс выставляет себя на window.
import '../../apiClient.js';        // → window.APIClient
import '../../errorBoundary.js';    // → window.ErrorBoundary
import '../../js/CacheManager.js';  // → window.CacheManager
import '../../js/SyncManager.js';   // → window.SyncManager
import '../../js/APIClientV1.js';   // → window.APIClientV1

// ─── 4. Init last — связывает всё вместе ─────────────────────────────────────
//   Создаёт инстансы (window.apiClient, window.QuoteCalc, и т.д.),
//   перехватывает методы для серверной работы, запускает sync.
//   Использует window.* для всех внешних классов (см. Phase 2 коммит).
import './init.js';

// ─── 4a. Estimate Lifecycle Status Bar (Sprint 19) ───────────────────────────
import './app/estimate-status-bar.js';   // → window.EstimateStatusBar

// ─── 4b. Modal keyboard (ESC) — Sprint 19d ───────────────────────────────────
import './modal-keyboard.js';

// ─── 4b.1. Inline handlers (Sprint 15 C.2) — CSP nonce migration ─────────────
//   Заменяет ~57 inline onclick/onchange/oninput атрибутов в index.html на
//   event delegation через data-action="...". Подключается ПОСЛЕ init.js,
//   но listener'ы навешиваются сразу на document (на момент клика QuoteCalc
//   уже создан в init.js — на DOMContentLoaded/load).
import './inline-handlers.js';

// ─── 4c. EstimateTemplate (Sprint 19e) — shared HTML-renderer ───────────────
//   PdfService.js использует напрямую (server-side).
//   Browser print() пока имеет свой код — TODO унификация с регрессионными тестами.
//   Здесь подключаем как window.EstimateTemplate для DevTools/проверки в browser.
import * as EstimateTemplate from '../../services/EstimateTemplate.js';
if (typeof window !== 'undefined') {
    window.EstimateTemplate = EstimateTemplate;
}

// ─── 5. CRM Shell (sidebar + router) ─────────────────────────────────────────
//   Phase 1 of CRM transformation (2026-05-07).
//   Калькулятор остаётся как есть, добавляется side-nav и роутинг.
//   Ждём `window.load` — init.js инициализирует apiClient/QuoteCalc именно там.
import { initCrmShell } from './crm-shell/shell.js';
if (document.readyState === 'complete') {
    initCrmShell();
} else {
    window.addEventListener('load', initCrmShell, { once: true });
}

// Маркер для отладки/проверки в консоли — что entry.js действительно загрузился.
console.log('[entry] ESM bootstrap loaded successfully');
